// Formatting helpers for the Pixel Tracking reports UI.
//
// Timestamps arrive in two shapes from the backend:
//  - client_ts_ist / received_at_ist on individual events: a plain
//    "dd-mm-yyyy HH:mm:ss" IST string. A Date constructor does not parse
//    this format reliably, so it's split apart manually.
//  - last_seen on the list endpoints: a real Date, serialized as an ISO
//    8601 UTC string by Express/Mongo. That one is parsed as a Date and
//    rendered in IST for consistency with the rest of the page.

const DDMMYYYY = /^(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})$/;

function to12h(HH, MM, SS) {
  let h = parseInt(HH, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${MM}:${SS} ${ampm}`;
}

export function formatTimestamp(raw) {
  if (!raw) return "";

  const m = String(raw).match(DDMMYYYY);
  if (m) {
    const [, dd, mm, yyyy, HH, MM, SS] = m;
    return `${dd}-${mm}-${yyyy} ${to12h(HH, MM, SS)}`;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  return `${get("day")}-${get("month")}-${get("year")} ${to12h(get("hour"), get("minute"), get("second"))}`;
}

const CLICK_FIELDS = [
  ["text", "Text"],
  ["alt", "Alt"],
  ["href", "Link"],
  ["src", "Source"],
  ["title", "Title"],
  ["class", "Class"],
  ["id", "ID"],
];

const LINK_FIELDS = new Set(["href", "src"]);

function present(val) {
  return val !== undefined && val !== null && String(val).trim() !== "";
}

// Returns an array of { label, value, link } rows for an event's `props`,
// formatted per event_name rather than dumped as raw JSON. `link: true`
// means the value is a URL worth rendering as a clickable anchor.
export function formatEventProps(eventName, props) {
  const p = props || {};

  switch (eventName) {
    case "click_any": {
      const rows = [];
      if (present(p.tag)) rows.push({ label: "Clicked", value: p.tag });
      CLICK_FIELDS.forEach(([key, label]) => {
        if (present(p[key])) rows.push({ label, value: p[key], link: LINK_FIELDS.has(key) });
      });
      return rows.length ? rows : [{ label: "Clicked", value: "" }];
    }

    case "scroll":
      return present(p.depth) ? [{ label: "Scrolled to", value: `${p.depth}%` }] : [];

    case "page_leave": {
      const rows = [];
      if (present(p.engaged_ms)) rows.push({ label: "Engaged", value: `${Math.round(p.engaged_ms / 1000)}s` });
      if (present(p.scroll_pct)) rows.push({ label: "Scrolled to", value: `${p.scroll_pct}%` });
      return rows;
    }

    case "page_view":
      return [];

    default: {
      const keys = Object.keys(p).filter((k) => present(p[k]));
      return keys.map((k) => ({ label: k, value: p[k] }));
    }
  }
}
